import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    FormControlLabel,
    LinearProgress,
    Paper,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { createTheme as createAppTheme } from '@/theme'

const ThemeLivePreview = ({ colorPreset, styleConfig, themeValue }) => {
    const previewTheme = useMemo(
        () =>
            createAppTheme({
                ...styleConfig,
                colorPreset,
                theme: themeValue
            }),
        [
            colorPreset,
            styleConfig.containerStretch,
            styleConfig.contrast,
            styleConfig.direction,
            styleConfig.layout,
            styleConfig.navColor,
            styleConfig.responsiveFontSizes,
            styleConfig.roundedCorners,
            themeValue
        ]
    )

    return (
        <ThemeProvider theme={previewTheme}>
            <Paper
                variant="outlined"
                sx={{
                    bgcolor: 'background.default',
                    borderColor: 'divider',
                    overflow: 'hidden'
                }}
            >
                <Box
                    sx={{
                        alignItems: 'center',
                        bgcolor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        gap: 1,
                        px: 1.5,
                        py: 1
                    }}
                >
                    <Chip color="primary" label="Live Theme" size="small" />
                    <TextField
                        size="small"
                        defaultValue="Search power meters"
                        sx={{
                            flexGrow: 1,
                            '& .MuiInputBase-root': { height: 32 }
                        }}
                    />
                    <Switch defaultChecked size="small" />
                </Box>

                <Tabs
                    value={0}
                    variant="fullWidth"
                    sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                >
                    <Tab label="Overview" />
                    <Tab label="Alerts" />
                    <Tab label="Usage" />
                </Tabs>

                <Box
                    sx={{
                        display: 'grid',
                        gap: 1.5,
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'minmax(0, 1fr) minmax(0, 1fr)'
                        },
                        p: 1.5
                    }}
                >
                    <Card elevation={1}>
                        <CardHeader
                            title="Site Summary"
                            subheader="Card style by theme"
                        />
                        <CardContent sx={{ pt: 0 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 1 }}
                            >
                                Daily completion
                            </Typography>
                            <LinearProgress
                                value={68}
                                variant="determinate"
                                sx={{ height: 8 }}
                            />
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 1.5 }}
                            >
                                2 sync jobs in progress
                            </Typography>
                        </CardContent>
                        <CardActions>
                            <Button size="small" variant="contained">
                                Primary
                            </Button>
                            <Button size="small" variant="text" color="inherit">
                                Details
                            </Button>
                        </CardActions>
                    </Card>

                    <Stack spacing={1.25}>
                        <Typography variant="subtitle2">Status</Typography>
                        <Alert severity="info" variant="outlined">
                            Sync running for 2 locations
                        </Alert>
                        <Divider />
                        <Stack direction="row" gap={1} flexWrap="wrap">
                            <Button size="small" variant="outlined">
                                Outlined
                            </Button>
                            <Button size="small" color="inherit">
                                Text
                            </Button>
                        </Stack>
                        <FormControlLabel
                            control={<Switch defaultChecked size="small" />}
                            label="Push alerts"
                            sx={{ m: 0 }}
                        />
                        <FormControlLabel
                            control={<Switch size="small" />}
                            label="Email digests"
                            sx={{ m: 0 }}
                        />
                    </Stack>
                </Box>
            </Paper>
        </ThemeProvider>
    )
}

ThemeLivePreview.propTypes = {
    colorPreset: PropTypes.string.isRequired,
    styleConfig: PropTypes.shape({
        containerStretch: PropTypes.bool,
        contrast: PropTypes.string,
        direction: PropTypes.string,
        layout: PropTypes.string,
        navColor: PropTypes.string,
        responsiveFontSizes: PropTypes.bool,
        roundedCorners: PropTypes.bool
    }).isRequired,
    themeValue: PropTypes.string.isRequired
}

export default ThemeLivePreview
